import React, { useEffect, useState } from 'react';
import { Star, Search, MessageSquare, Flag, Package, Zap, BookOpen } from 'lucide-react';
import { getSellerRatings, replyToReview } from '../services/api';

interface ReviewMetrics {
  packaging: number;
  speed: number;
  condition: number;
  communication: number;
}

interface Review {
  id: string;
  buyer_name: string;
  rating: number;
  date: string;
  book_title: string;
  metrics: ReviewMetrics;
  content: string;
  reply: string | null;
}

interface RatingsData {
  overall: number;
  count: number;
  breakdown: ReviewMetrics;
  distribution: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
  reviews: Review[];
}

const Reviews: React.FC = () => {
  const [data, setData] = useState<RatingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [filter, setFilter] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    fetchRatings();
  }, []);

  const fetchRatings = async () => {
    try {
      setLoading(true);
      const res = await getSellerRatings();
      setData(res);
    } catch (err: any) {
      setError(err.message || 'Failed to load ratings');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleReplySubmit = async (reviewId: string) => {
    if (!replyText.trim()) return;
    
    try {
      setSubmittingReply(true);
      const res = await replyToReview(reviewId, replyText);
      
      // Update local state
      if (data) {
        setData({
          ...data,
          reviews: data.reviews.map(r => 
            r.id === reviewId ? { ...r, reply: replyText } : r
          )
        });
      }
      
      showToast(res.message || 'Reply posted!');
      setReplyingTo(null);
      setReplyText('');
    } catch (err) {
      showToast('Failed to post reply', 'error');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleFlag = () => {
    showToast('Review flagged for moderation', 'success');
  };

  const filteredReviews = data?.reviews.filter(review => {
    const matchesFilter = filter === 'All' || review.rating.toString() === filter;
    const matchesSearch = review.content.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          review.buyer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          review.book_title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  }) || [];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-900"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center text-red-500 p-8">
        <p>{error || 'No data found.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white font-medium z-50 transition-opacity ${toast.type === 'success' ? 'bg-[#1D9E75]' : 'bg-[#D85A30]'}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-teal-900">Customer Reviews</h1>
          <p className="text-gray-500 mt-1">Manage feedback and your seller reputation</p>
        </div>
      </div>

      {/* SUMMARY CARD */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col md:flex-row border border-gray-100">
        {/* Left: Overall & Distribution */}
        <div className="p-8 md:w-1/2 md:border-r border-gray-100 flex flex-col">
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-[#F1EFE8] text-[#085041] w-24 h-24 rounded-2xl flex flex-col items-center justify-center">
              <span className="text-4xl font-bold">{data.overall.toFixed(1)}</span>
              <span className="text-sm font-medium opacity-80 mt-1">Overall</span>
            </div>
            <div>
              <div className="flex text-amber-400 text-xl mb-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star key={star} className={star <= Math.round(data.overall) ? "fill-amber-400" : "fill-gray-200 text-gray-200"} size={24} />
                ))}
              </div>
              <p className="text-gray-500 text-sm">Based on {data.count} reviews</p>
            </div>
          </div>
          
          <div className="space-y-2 mt-auto">
            {[5, 4, 3, 2, 1].map(star => {
              const count = data.distribution[star as keyof typeof data.distribution];
              const percentage = (count / data.count) * 100 || 0;
              return (
                <div key={star} className="flex items-center text-sm">
                  <span className="w-12 text-gray-500 tabular-nums">{star} ★</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full mx-3 overflow-hidden">
                    <div 
                      className="h-full bg-amber-400 rounded-full" 
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-10 text-right text-gray-400">{Math.round(percentage)}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Category Breakdown */}
        <div className="p-8 md:w-1/2 bg-gray-50 flex flex-col justify-center">
          <h3 className="text-gray-700 font-bold mb-6">Rating Breakdown</h3>
          <div className="grid grid-cols-1 gap-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600 w-32">
                <Package size={18} className="text-[#085041]" /> Packaging
              </div>
              <div className="flex-1 max-w-[200px] h-2 bg-gray-200 rounded-full mx-4 overflow-hidden">
                <div className="h-full bg-[#1D9E75] rounded-full" style={{ width: `${(data.breakdown.packaging / 5) * 100}%` }} />
              </div>
              <span className="font-semibold text-gray-700 w-8">{data.breakdown.packaging.toFixed(1)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600 w-32">
                <Zap size={18} className="text-[#085041]" /> Speed
              </div>
              <div className="flex-1 max-w-[200px] h-2 bg-gray-200 rounded-full mx-4 overflow-hidden">
                <div className="h-full bg-[#1D9E75] rounded-full" style={{ width: `${(data.breakdown.speed / 5) * 100}%` }} />
              </div>
              <span className="font-semibold text-gray-700 w-8">{data.breakdown.speed.toFixed(1)}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600 w-32">
                <BookOpen size={18} className="text-[#085041]" /> Condition
              </div>
              <div className="flex-1 max-w-[200px] h-2 bg-gray-200 rounded-full mx-4 overflow-hidden">
                <div className="h-full bg-[#1D9E75] rounded-full" style={{ width: `${(data.breakdown.condition / 5) * 100}%` }} />
              </div>
              <span className="font-semibold text-gray-700 w-8">{data.breakdown.condition.toFixed(1)}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600 w-32">
                <MessageSquare size={18} className="text-[#085041]" /> Comms
              </div>
              <div className="flex-1 max-w-[200px] h-2 bg-gray-200 rounded-full mx-4 overflow-hidden">
                <div className="h-full bg-[#1D9E75] rounded-full" style={{ width: `${(data.breakdown.communication / 5) * 100}%` }} />
              </div>
              <span className="font-semibold text-gray-700 w-8">{data.breakdown.communication.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex gap-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0 hide-scrollbar">
          {['All', '5', '4', '3', '2', '1'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                filter === f 
                  ? 'bg-[#085041] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'All' ? 'All Reviews' : `${f} ★`}
            </button>
          ))}
        </div>
        
        <div className="relative w-full sm:w-72">
          <input
            type="text"
            placeholder="Search reviews..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#085041]/20 focus:border-[#085041] transition-all"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
        </div>
      </div>

      {/* REVIEWS LIST */}
      <div className="space-y-4">
        {filteredReviews.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 bg-[#F1EFE8] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              ⭐
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No reviews found!</h3>
            <p className="text-gray-500">
              {searchQuery || filter !== 'All' 
                ? "Try adjusting your filters or search query." 
                : "Complete orders to receive reviews."}
            </p>
          </div>
        ) : (
          filteredReviews.map(review => (
            <div key={review.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 transition-all hover:shadow-md">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#085041] text-white rounded-full flex items-center justify-center font-bold">
                    {review.buyer_name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{review.buyer_name}</h4>
                    <p className="text-sm text-gray-500">"{review.book_title}" · {new Date(review.date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex text-amber-400">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star key={star} className={star <= review.rating ? "fill-amber-400" : "text-gray-200"} size={16} />
                  ))}
                </div>
              </div>

              {/* Metrics Pills */}
              <div className="flex flex-wrap gap-3 mb-4">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 text-xs font-medium text-gray-600 border border-gray-100">
                  📦 {review.metrics.packaging.toFixed(1)}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 text-xs font-medium text-gray-600 border border-gray-100">
                  ⚡ {review.metrics.speed.toFixed(1)}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 text-xs font-medium text-gray-600 border border-gray-100">
                  📖 {review.metrics.condition.toFixed(1)}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 text-xs font-medium text-gray-600 border border-gray-100">
                  💬 {review.metrics.communication.toFixed(1)}
                </span>
              </div>

              <p className="text-gray-700 leading-relaxed mb-4">
                "{review.content}"
              </p>

              {/* Reply Section */}
              {review.reply ? (
                <div className="bg-[#F1EFE8]/50 p-4 rounded-lg flex gap-3 border border-[#F1EFE8]">
                  <div className="w-8 h-8 shrink-0 bg-[#085041] text-white rounded-full flex items-center justify-center font-bold text-sm">
                    S
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#085041] mb-1">Your reply</p>
                    <p className="text-sm text-gray-700">{review.reply}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center mt-4">
                    <button 
                      onClick={() => { setReplyingTo(review.id); setReplyText(''); }}
                      className="text-[#085041] font-medium text-sm flex items-center gap-1.5 hover:underline"
                    >
                      <MessageSquare size={16} /> Reply to Review
                    </button>
                    <button 
                      onClick={handleFlag}
                      className="text-gray-400 hover:text-[#D85A30] transition-colors"
                      title="Flag inappropriate review"
                    >
                      <Flag size={16} />
                    </button>
                  </div>
                  
                  {/* Reply Input Area */}
                  {replyingTo === review.id && (
                    <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-100 animate-in fade-in slide-in-from-top-2">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write your public reply..."
                        className="w-full p-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#085041] focus:border-transparent resize-none text-sm"
                        rows={3}
                      />
                      <div className="flex justify-end gap-2 mt-3">
                        <button
                          onClick={() => setReplyingTo(null)}
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleReplySubmit(review.id)}
                          disabled={submittingReply || !replyText.trim()}
                          className="px-4 py-2 text-sm bg-[#085041] text-white hover:bg-[#063b30] rounded-lg transition-colors disabled:opacity-50"
                        >
                          {submittingReply ? 'Posting...' : 'Post Reply'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Reviews;
